/**
 * Die aktuellen Wochenziele ausserhalb der Planung (F-16):
 *
 * - `WochenzieleListe` im Block "Heute" der Uebersicht - anschauen und abhaken (V-02)
 * - `FokusDerWoche` im Morgenjournal - nur zur Erinnerung, ohne Bedienung
 */
import { Link } from 'react-router-dom';
import { Crosshair } from 'lucide-react';

import { useFokusZiele } from '@/modules/planning/usePlanung';
import { toggleFocusDone } from '@/db/repositories/focusGoals';
import { isoWeekKey } from '@/lib/datetime';
import { cn } from '@/lib/utils/cn';
import type { LocalDate } from '@/domain/types';

const planungPfad = (kw: string) => `/rueckblick?wochenplanung=${kw}`;

export function WochenzieleListe({ heute }: { heute: LocalDate }) {
  const kw = isoWeekKey(heute);
  const ziele = (useFokusZiele('woche', kw) ?? []).filter((g) => g.status === 'offen' || g.status === 'erledigt');
  const offen = ziele.filter((g) => g.status === 'offen').length;

  return (
    <section aria-label="Fokus dieser Woche" className="flex flex-col gap-1.5">
      <h3 className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-ink-faint">
        <Crosshair className="size-3.5" aria-hidden="true" /> Fokus dieser Woche
        {ziele.length > 0 && <span className="tabular-nums normal-case">· {ziele.length - offen}/{ziele.length}</span>}
        <Link to={planungPfad(kw)} className="ml-auto font-normal normal-case tracking-normal hover:text-ink hover:underline">Wochenplanung</Link>
      </h3>
      {ziele.length === 0 ? (
        <p className="text-sm text-ink-faint">Noch keine Wochenziele – <Link to={planungPfad(kw)} className="underline-offset-2 hover:underline">Woche planen</Link>.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {ziele.map((g) => (
            <li key={g.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={g.status === 'erledigt'} onChange={() => void toggleFocusDone(g.id)}
                aria-label={`${g.title} erledigt`} className="size-4 shrink-0 accent-[var(--color-accent)]" />
              <span className={cn('min-w-0 flex-1 truncate', g.status === 'erledigt' && 'text-ink-faint line-through')}>{g.title}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function FokusDerWoche({ date }: { date: LocalDate }) {
  const kw = isoWeekKey(date);
  const offen = (useFokusZiele('woche', kw) ?? []).filter((g) => g.status === 'offen');
  if (offen.length === 0) return null;

  return (
    <section aria-label="Fokus dieser Woche" className="flex flex-col gap-1.5 rounded-md border border-line bg-surface-raised/50 px-3.5 py-3">
      <h3 className="flex items-center gap-2 text-[12px] font-semibold text-ink-muted">
        <Crosshair className="size-3.5" /> Fokus dieser Woche
        <Link to={planungPfad(kw)} className="ml-auto text-[11px] font-normal text-ink-faint hover:text-ink hover:underline">Wochenplanung</Link>
      </h3>
      <ul className="flex list-disc flex-col gap-0.5 pl-5 text-sm">
        {offen.map((g) => <li key={g.id}>{g.title}</li>)}
      </ul>
    </section>
  );
}
